import { Star } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { cn } from '@/shared/ui';

interface StarRatingInputProps {
  readonly value: number;
  readonly onChange: (value: number) => void;
}

/** 별점마다 함께 보여 주는 한마디 — 0점(미선택)은 안내 문구. */
const RATING_WORDS = [
  '별을 눌러 만족도를 알려 주세요',
  '별로예요',
  '아쉬워요',
  '괜찮아요',
  '좋아요',
  '아주 좋아요',
];

/** 큰 별 다섯 개 — 올려 둔 별까지 미리 칠하고, 옆에 고른(또는 올려 둔) 점수의 한마디를 띄운다. */
export const StarRatingInput = ({ value, onChange }: StarRatingInputProps): ReactElement => {
  const [hovered, setHovered] = useState(0);
  const shown = hovered || value;

  return (
    <fieldset className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
      <legend className="sr-only">별점</legend>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <label
            key={n}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            className="cursor-pointer rounded-full p-0.5 transition-transform hover:scale-110 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-foreground"
          >
            <input
              type="radio"
              name="review-rating"
              className="sr-only"
              aria-label={`별점 ${n}점`}
              checked={value === n}
              onChange={() => onChange(n)}
            />
            <Star
              aria-hidden
              strokeWidth={1.5}
              className={cn(
                'size-10 transition-colors',
                n <= shown ? 'fill-foreground text-foreground' : 'fill-muted text-muted',
              )}
            />
          </label>
        ))}
      </div>
      <p
        aria-hidden
        className={cn(
          'text-sm',
          shown === 0 ? 'text-muted-foreground' : 'font-semibold text-foreground',
        )}
      >
        {shown > 0 && <span className="mr-2 tabular-nums">{shown}.0</span>}
        {RATING_WORDS[shown]}
      </p>
    </fieldset>
  );
};
